<template>
    <div class="WorkOrderMaterialList">
        <div v-for="(row, index) in rows" :key="index" class="materialItem">
            <div class="materialItemMain">
                <div class="materialColumn">
                    <FormSelectMaterial
                        :value.number="row.materialId"
                        label="Material"
                        @input="handleUpdateRow(index, { materialId: $event })"
                        showCreateOption
                    />
                </div>
                <div>
                    <FormTonsInput
                        v-if="row.type === 'tons'"
                        label="Quantity"
                        :value="row.quantity"
                        min="0"
                        @input="handleUpdateRow(index, { quantity: $event })"
                    />
                    <FormNumber
                        v-else
                        label="Quantity"
                        :value="row.quantity"
                        min="0"
                        placeholder="0"
                        @input="handleUpdateRow(index, { quantity: $event })"
                    />
                </div>
                <div>
                    <FormSelect
                        :value="row.type"
                        label="Unit"
                        @input="handleUpdateUnit(index, $event)"
                    >
                        <option value="tons">Tons</option>
                        <option value="loads">Loads</option>
                    </FormSelect>
                </div>
                <div v-if="showRates">
                    <FormCurrency
                        :label="row.type === 'tons' ? 'Rate per Ton' : 'Rate per Load'"
                        :value="row.rate"
                        :min="0"
                        @input="handleUpdateRow(index, { rate: $event })"
                    />
                </div>
            </div>

            <div class="deleteBtn" @click="handleRemoveRow(row, index)">
                <Icon name="trash" />
            </div>
        </div>

        <div v-if="!rows.length" class="empty">No materials have been added yet.</div>

        <div v-if="hasMaterialConflicts" class="error">Warning: The same material is listed more than once.</div>

        <Button class="outline" @click="handleAddRow">Add material</Button>

        <div class="toggleRow">
            <h4>Limit drivers to these materials</h4>
            <p>By default, <u>all materials</u> are available in the DumpTek mobile app. Enable this option so drivers can only pick the materials listed above for this work order.</p>
            <FormToggle :value="isRestricted" @input="handleToggleRestricted"/>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import FormSelectMaterial from '@/components/common/FormSelectMaterial';

// One row per material on the work order. `type` is the unit for both the quantity and
// the rate; a tons quantity is kept in pounds (like load tons) and a loads quantity is a
// count. Rates only matter when the work order is priced by material, so they are hidden
// otherwise but never cleared.
export default {
    name: 'WorkOrderMaterialList',

    components: { FormSelectMaterial },

    props: {
        materialRates: { type: Array, default: () => [] },
        // Driver restriction list. Kept in step with the rows while the toggle is on.
        materials: { type: Array, default: () => [] },
        rateType: { type: String, default: 'hourly' },
    },

    data: () => ({
        isRestricted: false,
    }),

    computed: {
        ...mapState('company', { allMaterials: 'materials' }),

        // Work orders that never had rates can still arrive with a null column.
        rows() {
            return this.materialRates || [];
        },

        showRates() {
            return this.rateType === 'material';
        },

        hasMaterialConflicts() {
            const seen = new Set();
            for (const row of this.rows) {
                if (row.materialId == null) continue;
                if (seen.has(row.materialId)) return true;
                seen.add(row.materialId);
            }

            return false;
        },
    },

    methods: {
        handleAddRow() {
            this.$analytics.trackButtonClick(this.$options.name, 'add_material_rate_row', {
                model_type: 'material',
            });

            this.updateRows([
                ...this.rows,
                { materialId: null, type: 'tons', rate: 0, quantity: null },
            ]);
        },

        handleRemoveRow(row, index) {
            this.$analytics.trackButtonClick(this.$options.name, 'remove_material_rate_row', {
                model_type: 'material',
                material_id: row.materialId,
            });

            this.updateRows(this.rows.filter((_, i) => i !== index));
        },

        handleUpdateRow(index, update) {
            this.updateRows(this.rows.map((row, i) => {
                if (i !== index) return row;
                return { ...row, ...update };
            }));
        },

        // Pounds and load counts do not convert into each other, so a unit change starts
        // the quantity over.
        handleUpdateUnit(index, type) {
            this.handleUpdateRow(index, { type, quantity: null });
        },

        handleToggleRestricted(isEnabled) {
            this.$analytics.trackButtonClick(this.$options.name, 'toggle_limit_materials', {
                model_type: 'material',
                is_enabled: isEnabled,
            });

            this.isRestricted = isEnabled;
            this.syncRestriction(this.rows);
        },

        updateRows(rows) {
            this.$emit('update:materialRates', rows);
            this.syncRestriction(rows);
        },

        syncRestriction(rows) {
            const wantedIds = this.isRestricted
                ? [...new Set(rows.map(row => row.materialId).filter(id => id != null))]
                : [];
            const currentIds = (this.materials || []).map(material => material.id);

            const sortedWanted = [...wantedIds].sort((a, b) => a - b);
            const sortedCurrent = [...currentIds].sort((a, b) => a - b);
            if (sortedWanted.length === sortedCurrent.length
                && sortedWanted.every((id, i) => id === sortedCurrent[i])) {
                return;
            }

            this.$emit('update:materials', wantedIds.map(id => (
                this.allMaterials.find(material => material.id === id) || { id }
            )));
        },
    },

    mounted() {
        this.isRestricted = Boolean(this.materials?.length);
    },
};
</script>

<style lang="scss" scoped>
@import 'globals.scss';

.WorkOrderMaterialList {
    .materialItem {
        display: flex;
        align-items: center;

        .materialItemMain {
            display: flex;
            flex: 1;

            & > div {
                flex: 1;
                margin-right: $grid-gutter-width;
            }

            .materialColumn {
                flex: 2;
            }
        }

        .deleteBtn {
            opacity: 0.25;
            cursor: pointer;

            &:hover {
                color: $red-color;
                opacity: 1;
            }
        }
    }

    .deleteBtn {
        display: inline-block;
    }

    .FormInputContainer {
        margin-bottom: $grid-gutter-width;
    }

    .Button {
        display: block;
        margin-left: auto;
    }

    .empty {
        margin: 15px 0;
        text-align: center;
        font-size: 18px;
    }

    .error {
        color: $red-color;
    }

    .toggleRow {
        margin-top: 30px;

        p {
            margin: 5px 0;
            font-size: 14px;
            color: $text-color-muted;
        }
    }
}
</style>
